import VimeoVideo from 'coreClasses/vimeo/VimeoVideo';
import ThronVideo from 'coreClasses/thron/ThronVideo';
import CloudflareStreamVideo from 'coreClasses/cloudflare-stream/CloudflareStreamVideo';
import {EVideoProvider, TAspectRatioResponse} from 'coreClasses/video/IAbstractVideo';
import {useServerData} from 'coreHooks/index';

type VideoAspectRatioParams = {
	provider: string;
	videoId: string;
	mobileVideoId?: string;
};

type VideoAspectRatioResult = {
	aspectRatio: string | null;
	aspectRatioMobile: string | null;
};

export const fetchVideoAspectRatio = async (provider: string, videoId: string): Promise<string | null> => {
	if (!provider || !videoId) {
		return null;
	}

	try {
		let response: TAspectRatioResponse | null;

		switch (provider) {
			case EVideoProvider.VIMEO:
				response = await VimeoVideo.staticGetOriginalAspectRatio(videoId);

				break;
			case EVideoProvider.THRON:
			case 'Thron_Asset':
				response = await ThronVideo.staticGetOriginalAspectRatio(
					videoId.includes('/') ? videoId.split('/')[0] : videoId,
					videoId.includes('/') ? videoId.split('/')[1] : '',
				);

				break;
			case EVideoProvider.CLOUDFLARE_STREAM:
				response = await CloudflareStreamVideo.staticGetOriginalAspectRatio(videoId);

				break;
			default:
				return null;
		}

		return response && !response.error ? response.value : null;
	} catch (err) {
		console.error('[useVideoAspectRatio] fetchVideoAspectRatio failed', {provider, videoId, err});

		return null;
	}
};

const useVideoAspectRatio = ({provider, videoId, mobileVideoId}: VideoAspectRatioParams): VideoAspectRatioResult => {
	const {aspectRatio, aspectRatioMobile} = useServerData(
		'video-aspect-ratio',
		async () => {
			const [aspectRatio, aspectRatioMobile] = await Promise.all([
				!!provider && !!videoId && fetchVideoAspectRatio(provider, videoId),
				!!provider && !!mobileVideoId && fetchVideoAspectRatio(provider, mobileVideoId),
			]);

			return {aspectRatio, aspectRatioMobile};
		},
		[videoId, mobileVideoId],
	);

	return {aspectRatio, aspectRatioMobile};
};

export default useVideoAspectRatio;
